import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { endpoints } from '../utils/apiClient';

const Tournaments = () => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchTournaments();
  }, []);

  const fetchTournaments = async () => {
    try {
      console.log('Fetching tournaments list');
      const response = await api.get(endpoints.tournaments.list);
      console.log('Tournaments API response:', response);

      // Handle different response formats
      let tournamentsData = [];

      if (response && Array.isArray(response)) {
        // Direct array response
        tournamentsData = response;
      } else if (response && response.tournaments && Array.isArray(response.tournaments)) {
        // { tournaments: [...] } format
        tournamentsData = response.tournaments;
      } else if (response && typeof response === 'object') {
        // Try to find an array property that might contain tournaments
        const arrayProps = Object.keys(response).filter(key => Array.isArray(response[key]));
        if (arrayProps.length > 0) {
          // Use the first array property found
          tournamentsData = response[arrayProps[0]];
        }
      }

      console.log('Processed tournaments data:', tournamentsData);
      setTournaments(tournamentsData);
    } catch (error) {
      console.error('Error details:', error);
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
      }
      setError(error.message || 'Failed to fetch tournaments');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-white text-center">
        <div className="text-xl">Loading tournaments...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-white text-center">
        <div className="text-xl text-red-500">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-white">Tournaments</h1>
        {user && (
          <button
            onClick={() => navigate('/create-tournament')}
            className="px-4 py-2 bg-purple-600 rounded-lg hover:bg-purple-700 text-white"
          >
            Create Tournament
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {tournaments.map((tournament) => {
          // Safely extract tournament ID
          const tournamentId = tournament._id || tournament.id;
          if (!tournamentId) {
            console.error('Tournament missing ID:', tournament);
            return null; // Skip tournaments without ID
          }

          return (
            <div
              key={tournamentId}
              onClick={() => navigate(`/tournament/${tournamentId}`)}
              className="bg-gray-900 rounded-lg overflow-hidden hover:ring-2 hover:ring-purple-500 transition-all cursor-pointer">
              <img
                src={tournament.thumbnail || 'https://via.placeholder.com/640x360?text=Tournament'}
                alt={tournament.title || 'Tournament'}
                className="w-full h-48 object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/640x360?text=Tournament';
                }}
              />
              <div className="p-4">
                <h3 className="text-lg font-semibold text-white mb-2">{tournament.title || 'Unnamed Tournament'}</h3>
                <div className="space-y-2 text-sm text-gray-400">
                  <p className="flex justify-between">
                    <span>Game:</span>
                    <span className="text-white">{tournament.game || 'N/A'}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Prize Pool:</span>
                    <span className="text-green-400">{tournament.prize || 'TBA'}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Date:</span>
                    <span className="text-white">
                      {tournament.date ? new Date(tournament.date).toLocaleDateString() : 'TBA'}
                    </span>
                  </p>
                  <p className="flex justify-between">
                    <span>Participants:</span>
                    <span className="text-white">
                      {(tournament.participants?.length || 0)}/
                      {tournament.maxParticipants || 'Unlimited'}
                    </span>
                  </p>
                  <div className="pt-2">
                    <span className={`px-2 py-1 rounded text-xs ${
                      tournament.status === "Registration Open"
                        ? "bg-green-500/20 text-green-400"
                        : "bg-red-500/20 text-red-400"
                    }`}>
                      {tournament.status || 'Unknown Status'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {tournaments.length === 0 && (
        <div className="text-center text-gray-400 mt-8">
          <p className="text-xl">No tournaments available at the moment.</p>
          <p className="mt-2">Create your first tournament!</p>
        </div>
      )}
    </div>
  );
};

export default Tournaments;
